import { TranslationContext } from 'contexts/TranslationContext'
import { getAbsoluteAssetSize } from 'helpers/getAbsoluteAssetSize'
import React, { useContext, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { getReferenceVideoAsset } from 'selectors'
import { Button, FormControlLabel } from '@material-ui/core'
import { isEmpty } from 'lodash'
import { ReactComponent as LineWeightIcon } from '~/assets/settings/line_weight.svg'
import Accordion from '~/components/base/Expansion'
import ColorStyle from '~/components/Settings/ColorStyle'
import Form from '../../BaseAssetSettingsForm'
import Position from '~/components/Settings/Position'
import Rotation from '~/components/Settings/Rotation'
import Size from '~/components/Settings/Size'
import SliderSettings from './Fields/SliderSettings'
import Switch from '../../../base/Switch'
import useImageAssetFormHandlers from './useImageFormHandlers'
import ColorPicker from '../../../base/ColorPicker'
import { ReactComponent as PipetteIcon } from './Pipette.svg'
import { useDOMEvent, useKeyCodeListener, useTap } from '../../../../hooks'
import { useColorClient } from '../../../../hooks/useColorClient'

export default function ImageAssetSettingsForm(props) {
  const {
    /** @type {Assets.VideoAsset} */
    asset,
    onChange,
    ...rest
  } = props

  const {
    /** @type {ImageAssetSettings} */
    settings,
  } = asset

  const {
    rotation,
    flip,
    opacity,
    outlineColor,
    outlineWidth,
    shadowColor,
    shadowBlur,
    keepAspectRatio,
    originalSize,
    isChromaKeyEnabled,
    chromaKeyColor,
    similarity,
    smoothness,
    spillReduction,
    chromaKeyOpacity,
  } = settings

  const referenceAsset = useSelector(getReferenceVideoAsset)
  const size = getAbsoluteAssetSize(asset, referenceAsset)
  const { width, height } = size

  const {
    onChangeOutlineColor,
    onChangeOutlineWidth,
    onChangePosition,
    onChangeShadowBlur,
    onChangeShadowColor,
    onChangeSize,
    onChangeKeepAspectRatio,
    onChangeRotation,
    onChangeFlip,
    onChangeOpacity,
    onClickFitToFrame,
    onClickFillToFrame,
    onChangeChromaKeyEnabled,
    onChangeChromaKeyColor,
    onChangeSimilarity,
    onChangeSmoothness,
    onChangeSpillReduction,
    onChangeChromaKeyOpacity,
    onResetAll,
  } = useImageAssetFormHandlers(settings, onChange)

  const { t } = useContext(TranslationContext)

  const [ colorClient, setColorClient ] = useState({})

  const destroyColorClient = () => {
    setColorClient({})
  }

  const handlePickColor = useTap(onChangeChromaKeyColor, destroyColorClient)

  const handleClickPipette = () => {
    if (!isEmpty(colorClient)) {
      destroyColorClient()
    } else {
      // NOTE: timeout to prevent useDOMEvent effect
      setTimeout(() => {
        setColorClient({
          clientId: 'Chroma-key',
          cb: handlePickColor,
          params: { assetId: asset.id },
        })
      })
    }
  }

  useKeyCodeListener(React.useRef(document), 'Escape', destroyColorClient)
  useDOMEvent(useRef(document), 'click', e => {
    if (e.target.localName !== 'canvas' && e.target.className !== 'color-picker__btn') {
      destroyColorClient()
    }
  })
  useColorClient(colorClient)

  return (
    <Form
      {...rest}
      asset={asset}
      title={t('MEDIA_FORM_TITLE_SETTINGS_IMAGE')}
      description={asset.name}
    >
      <Accordion caption={t('MEDIA_FORM_CAPTION_SIZE')}>
        <Size
          onChangeSize={onChangeSize}
          onChangeKeepAspectRatio={onChangeKeepAspectRatio}
          width={width}
          height={height}
          originalSize={originalSize}
          keepAspectRatio={keepAspectRatio}
        />
      </Accordion>

      <Accordion caption={t('MEDIA_FORM_CAPTION_POSITION')}>
        <Position
          showFit
          position={settings.position}
          offset={settings.offset}
          rotation={settings.rotation}
          onClickFitToFrame={onClickFitToFrame}
          onClickFillToFrame={onClickFillToFrame}
          onChangePosition={onChangePosition}
          fillFrame={settings.fillFrame}
          fitToFrame={settings.fitToFrame}
        />
      </Accordion>

      <Accordion caption={t('MEDIA_FORM_CAPTION_ROTATION')}>
        <Rotation
          rotation={rotation}
          position={settings.position}
          offset={settings.offset}
          flip={flip}
          onChangeRotation={onChangeRotation}
          onChangePosition={onChangePosition}
          onChangeFlip={onChangeFlip}
        />
      </Accordion>

      <Accordion caption={t('MEDIA_FORM_CAPTION_SETTINGS')}>
        <SliderSettings
          initialValue={opacity}
          label={t('MEDIA_FORM_SLIDER_LABEL_OPACITY')}
          onValueChange={onChangeOpacity}
          maxValue={100}
          mask="NUM%"
        />
      </Accordion>

      <Accordion caption={t('MEDIA_FORM_CAPTION_OUTLINE')}>
        <ColorStyle
          defaultValue={outlineWidth}
          icon={<LineWeightIcon className="settings-input__icon" />}
          color={outlineColor}
          onChangeColor={onChangeOutlineColor}
          onChangeValue={onChangeOutlineWidth}
        />
      </Accordion>

      <If condition={__CFG__.MEDIA_SETTINGS.IMAGE_SHADOW}>
        <Accordion caption={t('MEDIA_FORM_CAPTION_SHADOW')}>
          <ColorStyle
            defaultValue={shadowBlur}
            icon={<div className="settings-input__icon">Blur</div>}
            color={shadowColor}
            onChangeColor={onChangeShadowColor}
            onChangeValue={onChangeShadowBlur}
          />
        </Accordion>
      </If>

      {__CFG__.IS_CHROMAKEY_ENABLED && (
        <Accordion caption={t('MEDIA_VIDEO_FORM_CAPTION_CHROMA_KEY')}>
          <div className="chroma-key-color">
            <FormControlLabel
              className="chroma-key-color_label"
              control={(
                <div className="chroma-key-color_switch">
                  <Switch
                    onChange={e => onChangeChromaKeyEnabled(e.target.checked)}
                    checked={isChromaKeyEnabled}
                  />
                </div>
              )}
            />
            <div className="flex j-space-between" style={{ width: '310px' }}>
              <ColorPicker
                Icon={PipetteIcon}
                color={chromaKeyColor}
                onChangeColor={onChangeChromaKeyColor}
                colorClient={colorClient}
                isIconActive={!isEmpty(colorClient)}
                onClickIcon={handleClickPipette}
                iconTooltip={t('MEDIA_VIDEO_FORM_TOOLTIP_PICK_COLOR_FROM_PREVIEW')}
              />
              <Button
                disabled={!isChromaKeyEnabled}
                color="primary"
                variant="text"
                size="small"
                onClick={onResetAll}
                style={{ textTransform: 'unset' }}
                title={t('MEDIA_VIDEO_FORM_BTN_RESET_ALL_SETTINGS_TOOLTIP')}
                data-lang-id="MEDIA_VIDEO_FORM_BTN_RESET_ALL_SETTINGS"
              >
                {t('MEDIA_VIDEO_FORM_BTN_RESET_ALL_SETTINGS')}
              </Button>
            </div>
          </div>
          <SliderSettings
            disabled={!isChromaKeyEnabled}
            initialValue={similarity}
            label={t('MEDIA_VIDEO_FORM_SLIDER_LABEL_SIMILARITY')}
            onValueChange={onChangeSimilarity}
            maxValue={1000}
          />
          <SliderSettings
            disabled={!isChromaKeyEnabled}
            initialValue={smoothness}
            label={t('MEDIA_VIDEO_FORM_SLIDER_LABEL_SMOOTHNESS')}
            onValueChange={onChangeSmoothness}
            maxValue={1000}
          />
          <SliderSettings
            disabled={!isChromaKeyEnabled}
            initialValue={spillReduction}
            label={t('MEDIA_VIDEO_FORM_SLIDER_LABEL_SPILL_REDUCTION')}
            onValueChange={onChangeSpillReduction}
            maxValue={1000}
          />
          <SliderSettings
            disabled={!isChromaKeyEnabled}
            initialValue={chromaKeyOpacity}
            label={t('MEDIA_FORM_SLIDER_LABEL_OPACITY')}
            onValueChange={onChangeChromaKeyOpacity}
            maxValue={100}
            mask="NUM%"
          />
        </Accordion>
      )}
    </Form>
  )
}
